import config from '../config';
import selectedEnvironment from '../env';

const API_URL = `${config[selectedEnvironment].API_URL}/ClientTransferRequests`;
const ACCOUNTANTS_URL = `${config[selectedEnvironment].API_URL}/Accountants`;

/**
 * Create a client transfer request (accountant only).
 * @param {string} token - Bearer token
 * @param {{ caseId: string, requestedToAccountantId?: string, notes?: string }} dto
 * @returns {{ success: boolean, data?: object, error?: string }}
 */
const Create = async (token, dto) => {
    try {
        const response = await fetch(`${API_URL}/Create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({
                caseId: dto.caseId,
                requestedToAccountantId: dto.requestedToAccountantId || null,
                notes: dto.notes || null,
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, error: data?.error || data?.message || `HTTP ${response.status}` };
        }
        return { success: true, data: data?.data };
    } catch (e) {
        return { success: false, error: e?.message || 'Request failed' };
    }
};

/** List transfer requests (admin: all, accountant: own). Returns { success, data: [] } */
const List = async (token) => {
    try {
        const response = await fetch(`${API_URL}/List`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({}),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, data: [], error: data?.error || `HTTP ${response.status}` };
        }
        return { success: true, data: Array.isArray(data?.data) ? data.data : [] };
    } catch (e) {
        return { success: false, data: [], error: e?.message };
    }
};

/** Assign destination (admin only). dto: { requestId, assignedToAccountantId } */
const AssignDestination = async (token, dto) => {
    try {
        const response = await fetch(`${API_URL}/AssignDestination`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify(dto),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, error: data?.error || data?.message || `HTTP ${response.status}` };
        }
        return { success: true };
    } catch (e) {
        return { success: false, error: e?.message };
    }
};

/** Approve request (admin only). dto: { requestId, notes? } */
const Approve = async (token, dto) => {
    try {
        const response = await fetch(`${API_URL}/Approve`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify(dto),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, error: data?.error || data?.message || `HTTP ${response.status}` };
        }
        return { success: true };
    } catch (e) {
        return { success: false, error: e?.message };
    }
};

/** Reject request (admin only). dto: { requestId, notes? } */
const Reject = async (token, dto) => {
    try {
        const response = await fetch(`${API_URL}/Reject`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify(dto),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, error: data?.error || data?.message || `HTTP ${response.status}` };
        }
        return { success: true };
    } catch (e) {
        return { success: false, error: e?.message };
    }
};

/** Get accountants list for assign dropdown (admin). */
const getAccountantsList = async (token) => {
    try {
        const response = await fetch(
            `${ACCOUNTANTS_URL}/AccountantsWithDetails?page=1&limit=1000`,
            { headers: { Authorization: 'Bearer ' + token } }
        );
        if (!response.ok) return [];
        const data = await response.json().catch(() => ({}));
        const items = data?.data ?? data?.items ?? (Array.isArray(data) ? data : []) ?? [];
        return Array.isArray(items) ? items : [];
    } catch (e) {
        return [];
    }
};

export default { Create, List, AssignDestination, Approve, Reject, getAccountantsList };
